import styles from './TireSearch.module.scss'
import React, { useCallback, useEffect } from 'react'
import SearchForm from '../SearchForm/SearchForm'
import TyreSizePicker, { TyreSizeCriteria } from '../TyreSizePicker/TyreSizePicker'
import DefaultInput from '@/components/inputs/DefaultInput/DefaultInput'
import { useTranslation } from 'next-i18next'
import { useLazyGetTyresQuery } from '@/api/tyres'
import SearchResult from '../SearchResult/SearchResult'
import { ArticleModel, TyreSearchModel } from '@/types/api'
import TyreFilter from '@/components/filter/TyreFilter/TyreFilter'
import Button from '@/components/buttons/Button/Button'
import useAnalyticsManager from '@/utils/hooks/useAnalyticsManager'
import TyreSearchQueryParams from '@/types/TyreSearchQueryParams'
import useUpdateQuery from '@/utils/hooks/useUpdateQuery'
import { filterObject } from '@/utils/helpers'
import { useRouter } from 'next/router'
import FilterChangeHandler from '@/types/FilterChangeHandler'

type Props = {
  onHasSearch?: (hasResult: boolean) => void
}

const TireSearch = (props: Props) => {
  const { t } = useTranslation()
  const [fetchTyres, result] = useLazyGetTyresQuery()
  const [criteria, setCriteria] = React.useState<TyreSearchModel>({
    tyreType: 0,
    diameter: undefined,
    width: undefined,
    aspectRatio: undefined,
    description: '',
  })

  const [filterdItems, setFilterdItems] = React.useState<any[]>()
  const analyticsManager = useAnalyticsManager()
  const { updateQuery } = useUpdateQuery<TyreSearchQueryParams>()
  const queryLoaded = React.useRef<boolean>(false)
  const router = useRouter()

  const buildQuery = useCallback((criteria: TyreSearchModel): TyreSearchQueryParams => {
    const params: TyreSearchQueryParams = {
      aspectRatio: criteria?.aspectRatio?.toString(),
      diameter: criteria?.diameter?.toString(),
      width: criteria?.width?.toString(),
      description: criteria?.description,
      tyretype: criteria?.tyreType?.toString(),
    }

    return filterObject(params)
  }, [])

  const rebuildQuery = useCallback((criteria: TyreSearchModel, overwriteQuery?: boolean) => {
    let query = buildQuery(criteria)
    updateQuery(query, overwriteQuery)
  }, [buildQuery, updateQuery])

  const onChangeHandler = async (payload: TyreSizeCriteria) => {
    const newCriteria = {
      ...criteria,
      ...payload,
      description: ''
    }

    setCriteria(newCriteria)
    search(newCriteria, true)
  }

  const search = useCallback((payload: TyreSearchModel, overwriteQuery?: boolean) => {
    if (payload?.diameter || payload.description) {
      fetchTyres(payload)
      setFilterdItems(undefined)
      analyticsManager.search(payload.description)

      rebuildQuery(payload, overwriteQuery)

      if (props.onHasSearch)
        props.onHasSearch(true)
    }
  }, [analyticsManager, fetchTyres, props, rebuildQuery])


  const handleOnSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    search(criteria, true)
  }

  const onChangeTextHandler = (value: string) => {
    setCriteria({
      ...criteria,
      description: value,
      diameter: undefined,
      width: undefined,
      aspectRatio: undefined,
    })
  }

  const resetCriteria = () => {
    setCriteria({
      tyreType: 0,
      diameter: undefined,
      width: undefined,
      aspectRatio: undefined,
      description: '',
    })
  }

  const onFilterChange: FilterChangeHandler<ArticleModel> = (items, query) => {
    updateQuery(query)
    setFilterdItems(items)
  }

  const updateStateFromQuery = useCallback((query: TyreSearchQueryParams) => {
    const newCriteria = {
      tyreType: query.tyretype ? parseInt(query.tyretype) : 0,
      diameter: query.diameter ? parseFloat(query.diameter) : undefined,
      width: query.width ? parseFloat(query.width) : undefined,
      aspectRatio: query.aspectRatio ? parseFloat(query.aspectRatio) : undefined,
      description: query.description || '',
    }

    search(newCriteria)
    setCriteria(newCriteria)
    queryLoaded.current = true
  }, [search])

  /**
   * Load query from URL and update state
   */
  useEffect(() => {
    if (!queryLoaded.current && router.isReady) {
      updateStateFromQuery(router.query)
    }
  }, [queryLoaded, router.isReady, router.query, updateStateFromQuery])

  const settings = {
    showBrand: true,
    showModel: false,
    showTyreInfo: false,
  }

  const items = filterdItems || result.data
  const searchComplete = result.data && !result.isFetching

  return (
    <div className={styles.component}>
      <SearchForm
        onSubmit={handleOnSubmit}
      >
        <TyreSizePicker
          tyreType={criteria.tyreType}
          diameter={criteria.diameter}
          width={criteria.width}
          aspectRatio={criteria.aspectRatio}
          onCriteriaChange={onChangeHandler}
        />
        <div className={styles.searchInput}>
          <DefaultInput
            searchIcon={true}
            name="description"
            placeholder={t('search.searchPlaceholderTyre') || ''}
            onChange={onChangeTextHandler}
            value={criteria.description}
            inputStyle="lightBackground"
          />
          <Button
            label={t('search.searchButton') || ''}
            rounded={true}
            type="submit"
          />
          <Button
            label={t('common:reset') || ''}
            dark={true}
            rounded={true}
            onClick={resetCriteria}
          />
        </div>
      </SearchForm>
      {searchComplete && (
        <TyreFilter
          items={result.data || []}
          onFilterChange={onFilterChange}
        />
      )}
      <SearchResult
        isFetching={result.isFetching}
        items={items}
        settings={settings}
        listId={'search_tyres'}
        listName={'Search tyres'}
      />
    </div>
  )
}

export default TireSearch